import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook to detect when an element enters the viewport
 * Uses Intersection Observer API for performance
 *
 * @param {Object} options - Intersection Observer options
 * @param {number} options.threshold - Percentage of element visible before triggering (0-1)
 * @param {string} options.rootMargin - Margin around viewport (e.g., "50px")
 * @param {boolean} options.triggerOnce - Whether to only trigger once (default: true)
 * @returns {Array} [ref, isInView] - Ref to attach to element and visibility state
 */
export const useInView = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true
  } = options;

  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  const hasTriggered = useRef(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) return;

    // If already triggered and triggerOnce is true, don't observe again
    if (hasTriggered.current && triggerOnce) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          hasTriggered.current = true;

          // Disconnect if triggerOnce is true
          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce) {
          // Only update if not triggerOnce mode
          setIsInView(false);
        }
      },
      {
        threshold,
        rootMargin
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [ref, isInView];
};

export default useInView;
